import { ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { useState } from 'react';
import { useFilterStore } from '../store/useFilterStore';

interface SidebarProps {
  sectors: string[];
  industries: string[];
  tags: { id: string; name: string; color?: string }[];
  isOpen: boolean;
  onClose: () => void;
}

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function FilterSection({ title, children, defaultOpen = true }: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b last:border-b-0" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 transition-colors group min-h-[44px]"
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.03)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <span className="font-semibold text-sm tracking-tight" style={{ color: '#F9F9F9' }}>{title}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 transition-colors" style={{ color: '#9E9E9E' }} />
        ) : (
          <ChevronDown className="w-4 h-4 transition-colors" style={{ color: '#9E9E9E' }} />
        )}
      </button>
      {isOpen && <div className="px-6 pb-5">{children}</div>}
    </div>
  );
}

interface CustomCheckboxProps {
  checked: boolean;
  onChange: () => void;
  label: string;
}

function CustomCheckbox({ checked, onChange, label }: CustomCheckboxProps) {
  return (
    <button
      onClick={onChange}
      className="w-full flex items-center gap-3 py-3 px-3 rounded-lg transition-all group text-left min-h-[44px]"
      style={{
        backgroundColor: checked ? 'rgba(62, 207, 142, 0.1)' : 'transparent'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = checked ? 'rgba(62, 207, 142, 0.15)' : 'rgba(255, 255, 255, 0.03)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = checked ? 'rgba(62, 207, 142, 0.1)' : 'transparent';
      }}
    >
      <div
        className="flex items-center justify-center w-4 h-4 rounded border-2 transition-all flex-shrink-0"
        style={{
          backgroundColor: checked ? '#3ECF8E' : 'transparent',
          borderColor: checked ? '#3ECF8E' : 'rgba(255, 255, 255, 0.2)'
        }}
      >
        {checked && <Check className="w-3 h-3" style={{ color: '#0F0F0F' }} strokeWidth={3} />}
      </div>
      <span
        className="text-sm transition-colors flex-1 leading-tight"
        style={{
          color: checked ? '#F9F9F9' : '#9E9E9E',
          fontWeight: checked ? 500 : 400
        }}
      >
        {label}
      </span>
    </button>
  );
}

export function Sidebar({ sectors, industries, tags, isOpen, onClose }: SidebarProps) {
  const {
    selectedSectors,
    selectedIndustries,
    selectedTags,
    scoreFilters,
    setSelectedSectors,
    setSelectedIndustries,
    setSelectedTags,
    setScoreFilter,
    resetFilters,
  } = useFilterStore();

  const hasActiveFilters =
    selectedSectors.length > 0 ||
    selectedIndustries.length > 0 ||
    selectedTags.length > 0 ||
    Object.values(scoreFilters).some((f) => f.min > 0 || f.max < 100);

  const toggleSector = (sector: string) => {
    if (selectedSectors.includes(sector)) {
      setSelectedSectors(selectedSectors.filter((s) => s !== sector));
    } else {
      setSelectedSectors([...selectedSectors, sector]);
    }
  };

  const toggleIndustry = (industry: string) => {
    if (selectedIndustries.includes(industry)) {
      setSelectedIndustries(selectedIndustries.filter((i) => i !== industry));
    } else {
      setSelectedIndustries([...selectedIndustries, industry]);
    }
  };

  const toggleTag = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter((t) => t !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };

  const scoreTiers = ['Good', 'Average', 'Poor'];
  const scoreTypes = [
    { key: 'value', label: 'Value' },
    { key: 'growth', label: 'Growth' },
    { key: 'health', label: 'Health' },
    { key: 'momentum', label: 'Momentum' },
  ] as const;

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed md:sticky top-0 left-0 z-50
          w-72 h-screen md:h-[calc(100vh-180px)] md:top-[180px]
          border-r overflow-y-auto scrollbar-thin
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        style={{ backgroundColor: '#1A1A1A', borderColor: 'rgba(255, 255, 255, 0.1)' }}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b flex items-center justify-between sticky top-0 backdrop-blur-md z-10" style={{ borderColor: 'rgba(255, 255, 255, 0.1)', backgroundColor: 'rgba(26, 26, 26, 0.95)' }}>
          <h2 className="font-semibold text-base" style={{ color: '#F9F9F9' }}>Filters</h2>
          <div className="flex items-center gap-3">
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="text-xs font-medium transition-colors px-3 py-2 rounded min-h-[44px]"
                style={{ color: '#3ECF8E' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#4DD99D';
                  e.currentTarget.style.backgroundColor = 'rgba(62, 207, 142, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#3ECF8E';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Clear all
              </button>
            )}
            {/* Close button for mobile */}
            <button
              onClick={onClose}
              className="md:hidden p-2 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              style={{ color: '#9E9E9E' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.color = '#F9F9F9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#9E9E9E';
              }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

      {/* Sectors */}
      {sectors.length > 0 && (
        <FilterSection title="Sector">
          <div className="space-y-1 max-h-64 overflow-y-auto scrollbar-thin pr-1">
            {sectors.map((sector) => (
              <CustomCheckbox
                key={sector}
                checked={selectedSectors.includes(sector)}
                onChange={() => toggleSector(sector)}
                label={sector}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Industries */}
      {industries.length > 0 && (
        <FilterSection title="Industry">
          <div className="space-y-1 max-h-64 overflow-y-auto scrollbar-thin pr-1">
            {industries.map((industry) => (
              <CustomCheckbox
                key={industry}
                checked={selectedIndustries.includes(industry)}
                onChange={() => toggleIndustry(industry)}
                label={industry}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Scores */}
      <FilterSection title="Scores">
        <div className="space-y-5">
          {scoreTypes.map(({ key, label }) => (
            <div key={key}>
              <label className="text-xs font-semibold mb-2.5 block tracking-wide uppercase" style={{ color: '#9E9E9E' }}>
                {label}
              </label>
              <div className="space-y-1">
                {scoreTiers.map((tier) => (
                  <CustomCheckbox
                    key={tier}
                    checked={scoreFilters[key].tiers.includes(tier)}
                    onChange={() => {
                      const currentTiers = scoreFilters[key].tiers;
                      const newTiers = currentTiers.includes(tier)
                        ? currentTiers.filter((t) => t !== tier)
                        : [...currentTiers, tier];
                      setScoreFilter(key, { ...scoreFilters[key], tiers: newTiers });
                    }}
                    label={tier}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </FilterSection>

      {/* Tags */}
      {tags.length > 0 && (
        <FilterSection title="Tags">
          <div className="space-y-1 max-h-64 overflow-y-auto scrollbar-thin pr-1">
            {tags.map((tag) => (
              <CustomCheckbox
                key={tag.id}
                checked={selectedTags.includes(tag.id)}
                onChange={() => toggleTag(tag.id)}
                label={tag.name}
              />
            ))}
          </div>
        </FilterSection>
      )}
      </aside>
    </>
  );
}

